// 計算エンジン中核（§5.3 / §5.4）。純粋・RNG 注入・DOM 非依存。
//
// 設計の要:
// - ドロップ行を fixed（直接 λ）と gamma（実績/実績＋予測の事後 Gamma）に分割。
// - f*/成長率プール revFull: 標本ごとに gamma 行の λ を引き直す（＝1周予測＝負の二項, §5.2.1）。
// - 破産シミュ: poolFixed（fixed 行＋安定収入のみ）を事前生成し、gamma 行は
//   トラジェクトリごとに λ を1回引いて全周固定する（campaign 相関, §5.4）。
// - 不変条件: B（現在軍資金）は一切入力にしない。B は ruin(B) と曲線マーカーにのみ使う。
import { bestF, F_MAX, meanLog } from './kelly';
import type { Stats } from './stats';
import type { EngineDropRow, EngineResult, FarmInput } from './types';

const DEFAULT_N = 150_000;
const DEFAULT_T = 500;
const CURVE_PTS = 20;
const MAXB_FACTOR = 1.3;
const H_MIN = 400;
const H_MAX = 1400;

export interface ComputeOptions {
  /** モンテカルロ標本数（既定 150k, 比較ページの概算で減らせる）。 */
  N?: number;
  /** 破産トラジェクトリ数（既定 500）。 */
  T?: number;
}

export interface ComputeOutput {
  /** シリアライズ可能な結果（postMessage で渡せる）。 */
  result: EngineResult;
  /**
   * 任意の B に対する破産確率。保持プールを再利用し標本を引き直さない。
   * Worker が保持して ruinAt RPC に使う（関数なので result には載らない）。
   */
  ruin: (B: number) => number;
}

interface GammaRow {
  alpha: number;
  beta: number;
  pBase: number;
}
interface FixedRow {
  lambda: number;
  pBase: number;
}

function lambdaHatOf(row: EngineDropRow): number {
  return row.rate.kind === 'fixed' ? row.rate.lambda : row.rate.lambdaHat;
}

/** 期待収入/周（base）: 安定収入 + Σ ドロップ p·λ̂ + Σ グループ期待値 + Σ 標本平均（§5.2.3）。 */
export function expectedIncome(input: FarmInput): number {
  let inc = input.Sbase;
  for (const d of input.drops) inc += d.pBase * lambdaHatOf(d);
  for (const g of input.groups ?? []) {
    let tot = 0;
    let wv = 0;
    for (const m of g.members) {
      const w = Math.max(0, m.weight);
      tot += w;
      wv += w * m.pBase;
    }
    inc += tot > 0 ? (g.m * wv) / tot : 0;
  }
  for (const s of input.samples ?? []) {
    if (s.values.length === 0) continue;
    let sum = 0;
    for (const v of s.values) sum += v;
    inc += sum / s.values.length;
  }
  return inc;
}

/** 最低収入/周（base）: 安定収入 + Σ グループ(m×最安メンバー) + Σ 標本最小値。Poisson 行は最悪0で寄与なし。 */
export function minIncome(input: FarmInput): number {
  let inc = input.Sbase;
  for (const g of input.groups ?? []) {
    if (g.members.length === 0) continue;
    let mn = Infinity;
    for (const m of g.members) mn = Math.min(mn, m.pBase);
    inc += g.m * (Number.isFinite(mn) ? mn : 0);
  }
  for (const s of input.samples ?? []) {
    if (s.values.length === 0) continue;
    let mn = Infinity;
    for (const v of s.values) mn = Math.min(mn, v);
    inc += mn;
  }
  return inc;
}

export function computeFarm(
  input: FarmInput,
  stats: Stats,
  opts: ComputeOptions = {},
): ComputeOutput {
  const N = opts.N ?? DEFAULT_N;
  const T = opts.T ?? DEFAULT_T;
  const { C, Sbase, drops } = input;
  const groups = input.groups ?? [];
  const samples = (input.samples ?? []).filter((s) => s.values.length > 0);

  // グループの抽選準備（累積重み・合計）。
  const groupPrep = groups.map((g) => {
    const cum: number[] = [];
    let total = 0;
    for (const mem of g.members) {
      total += Math.max(0, mem.weight);
      cum.push(total);
    }
    return { m: g.m, members: g.members, cum, total };
  });

  // 1周のグループ収入: 各グループから確定 m 個を重み比でカテゴリカル抽選（排他, §5.2.2）。
  const drawGroups = (): number => {
    let sum = 0;
    for (const gp of groupPrep) {
      if (gp.total <= 0) continue;
      for (let d = 0; d < gp.m; d++) {
        const u = stats.uniform() * gp.total;
        let idx = 0;
        while (idx < gp.cum.length - 1 && gp.cum[idx]! < u) idx++;
        sum += gp.members[idx]!.pBase;
      }
    }
    return sum;
  };

  // 1周の標本行収入: 各行の values から一様に1個（§5.2.3）。行が無ければ RNG を消費しない
  // （samples 未使用の既存セットで乱数列＝結果を変えないため）。
  const drawSamples = (): number => {
    let sum = 0;
    for (const s of samples) {
      sum += s.values[(stats.uniform() * s.values.length) | 0]!;
    }
    return sum;
  };

  // 期待量（点推定 λ̂）は常に算出（表示用・検証用, §5.5）。
  const income = expectedIncome(input);
  const minIncomeVal = minIncome(input);
  const EV = income - C;
  const roi = C > 0 ? EV / C : 0;

  const base = {
    C,
    Sbase,
    income,
    minIncome: minIncomeVal,
    EV,
    roi,
  };

  // 無効ケース（§5.3）: 推奨軍資金を出さない。
  const invalid = (reason: 'cost' | 'noitem' | 'ev' | 'rate'): ComputeOutput => ({
    result: {
      valid: false,
      reason,
      ...base,
      fStar: 0,
      fStarCapped: false,
      bankFull: 0,
      bankHalf: 0,
      bankQuarter: 0,
      growthHalf: 0,
      maxB: 0,
      H: 0,
      curve: [],
      ruinFull: 0,
      ruinHalf: 0,
      ruinQuarter: 0,
    },
    ruin: () => NaN,
  });

  // コスト行にレート未取得 = C が不完全で EV が過大方向に壊れている（§4.1）。除外して続行しない。
  if (input.costIncomplete) return invalid('rate');
  if (C <= 0) return invalid('cost');
  if (drops.length === 0 && groups.length === 0 && samples.length === 0) return invalid('noitem');
  if (EV <= 0) return invalid('ev');

  // fixed / gamma 行に分割。
  const fixedRows: FixedRow[] = [];
  const gammaRows: GammaRow[] = [];
  for (const d of drops) {
    if (d.rate.kind === 'fixed') fixedRows.push({ lambda: d.rate.lambda, pBase: d.pBase });
    else gammaRows.push({ alpha: d.rate.alpha, beta: d.rate.beta, pBase: d.pBase });
  }

  // プール生成: poolFixed（fixed+安定）と revFull（+gamma）。fixed の引きは両者で共有。
  const poolFixed = new Float64Array(N);
  const xc = new Float64Array(N);
  for (let i = 0; i < N; i++) {
    let baseRev = Sbase;
    for (const fr of fixedRows) {
      if (fr.lambda > 0) baseRev += stats.poisson(fr.lambda) * fr.pBase;
    }
    // グループ・標本行は不確実性なし（分布は固定）→ poolFixed に入れて両シミュで共用。
    baseRev += drawGroups();
    baseRev += drawSamples();
    poolFixed[i] = baseRev;
    let full = baseRev;
    for (const gr of gammaRows) {
      const lam = stats.gammaSample(gr.alpha, gr.beta);
      full += stats.poisson(lam) * gr.pBase;
    }
    xc[i] = (full - C) / C;
  }

  // ケリー f* と推奨軍資金。上限到達（境界解）は fStarCapped で識別できるようにする（§5.3）。
  const fStar = bestF(xc);
  const fStarCapped = fStar >= F_MAX - 1e-4;
  const bankFull = C / fStar;
  const bankHalf = (2 * C) / fStar;
  const bankQuarter = (4 * C) / fStar;
  const growthHalf = Math.exp(meanLog(fStar / 2, xc)) - 1;

  const maxB = bankQuarter * MAXB_FACTOR;
  const H = Math.min(H_MAX, Math.max(H_MIN, Math.ceil((maxB / EV) * 3)));

  // 破産確率（トラジェクトリ・シミュ）。gamma 行はトラジェクトリ毎に λ 固定。
  const hasGamma = gammaRows.length > 0;
  const ruin = (B0: number): number => {
    if (B0 < C) return 1;
    let ruined = 0;
    for (let t = 0; t < T; t++) {
      // この軌道での真のレートを1回だけ引いて固定（campaign 相関, §5.4）。
      const lamTraj = hasGamma ? gammaRows.map((g) => stats.gammaSample(g.alpha, g.beta)) : [];
      let bal = B0;
      for (let h = 0; h < H; h++) {
        bal -= C;
        if (bal < 0) {
          ruined++;
          break;
        }
        let inc = poolFixed[(stats.uniform() * N) | 0]!;
        for (let gi = 0; gi < gammaRows.length; gi++) {
          inc += stats.poisson(lamTraj[gi]!) * gammaRows[gi]!.pBase;
        }
        bal += inc;
      }
    }
    return ruined / T;
  };

  // 破産カーブ（B: 0〜maxB を約20点, §5.4）。
  const curve: Array<{ B: number; p: number }> = [];
  for (let i = 0; i <= CURVE_PTS; i++) {
    const B = (maxB * i) / CURVE_PTS;
    curve.push({ B, p: ruin(B) });
  }

  const result: EngineResult = {
    valid: true,
    ...base,
    fStar,
    fStarCapped,
    bankFull,
    bankHalf,
    bankQuarter,
    growthHalf,
    maxB,
    H,
    curve,
    ruinFull: ruin(bankFull),
    ruinHalf: ruin(bankHalf),
    ruinQuarter: ruin(bankQuarter),
  };

  return { result, ruin };
}
